import { useState, type CSSProperties } from 'react';

import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import contractTemplatesService, {
  type ContractTemplate,
} from '../../services/contractTemplates.service';
import { uploadProblem } from './contractTemplates.helpers';

/**
 * The two forms that let a super admin work without SQL (#277): make the contract template of a
 * product, and add a version to a template. A version is an uploaded PDF, or, with no file, the
 * generated placeholder agreement. Neither publishes anything:
 * publishing stays its own audited act on the template card.
 */

const field: CSSProperties = {
  width: '100%',
  marginTop: 4,
  padding: 8,
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  fontSize: 13,
};
const labelStyle: CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: colors.inkSoft };
const primary: CSSProperties = {
  padding: '6px 14px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  border: 'none',
  background: colors.navy,
  color: '#fff',
  cursor: 'pointer',
};
const secondary: CSSProperties = {
  ...primary,
  background: colors.surface,
  color: colors.ink,
  border: `1px solid ${colors.border}`,
};

function ErrorLine({ message }: Readonly<{ message: string | null }>): JSX.Element | null {
  if (!message) return null;
  return (
    <div
      role="alert"
      style={{
        background: colors.dangerBg,
        color: colors.danger,
        border: `1px solid ${colors.dangerBorder}`,
        borderRadius: 8,
        padding: '8px 10px',
        fontSize: 12,
        margin: '8px 0',
      }}
    >
      {message}
    </div>
  );
}

export function NewTemplatePanel({
  products,
  onCreated,
}: Readonly<{
  products: ReadonlyArray<{ product_id: string; product_name: string }>;
  onCreated: (template: ContractTemplate) => void;
}>): JSX.Element {
  const [productId, setProductId] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ready = productId !== '' && reason.trim().length >= 4 && !busy;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const made = await contractTemplatesService.create({
        product_id: productId,
        reason: reason.trim(),
        actor_password: password === '' ? undefined : password,
      });
      setProductId('');
      setReason('');
      setPassword('');
      onCreated(made);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Could not create the template.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 14,
        padding: 18,
        marginBottom: 16,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 700, color: colors.ink, margin: 0 }}>New product template</h2>
      <p style={{ fontSize: 12, color: colors.muted, margin: '4px 0 10px' }}>
        A product needs a contract template before it can be offered. Create it here, upload a version
        and publish it.
      </p>
      {products.length === 0 ? (
        <p style={{ fontSize: 13, color: colors.muted }}>Every product already has a template.</p>
      ) : (
        <>
          <ErrorLine message={err} />
          <label style={labelStyle}>
            <span>Product</span>
            <select value={productId} onChange={(e) => setProductId(e.target.value)} style={field}>
              <option value="">Choose a product…</option>
              {products.map((p) => (
                <option key={p.product_id} value={p.product_id} translate="no">
                  {p.product_name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ ...labelStyle, marginTop: 10 }}>
            <span>Reason (audited, min 4 chars)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} style={field} />
          </label>
          <label style={{ ...labelStyle, marginTop: 10 }}>
            <span>Your password (local accounts only)</span>
            <input
              type="password"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={field}
            />
          </label>
          <div style={{ marginTop: 12 }}>
            <button type="button" disabled={!ready} onClick={() => void submit()} style={primary}>
              {busy ? 'Working…' : 'Create template'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export function UploadVersionPanel({
  template,
  onUploaded,
  onClose,
}: Readonly<{
  template: ContractTemplate;
  onUploaded: (template: ContractTemplate) => void;
  onClose: () => void;
}>): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const problem = uploadProblem({ file, reason });

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const updated = await contractTemplatesService.uploadVersion(template.id, {
        reason: reason.trim(),
        actorPassword: password === '' ? undefined : password,
        file,
        changeNotes: notes.trim() === '' ? undefined : notes.trim(),
      });
      onUploaded(updated);
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Could not upload the version.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${colors.borderSoft}` }}>
      <ErrorLine message={err} />
      <label style={labelStyle}>
        <span>PDF (leave empty to store the generated placeholder)</span>
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{ ...field, padding: 6 }}
        />
      </label>
      <label style={{ ...labelStyle, marginTop: 10 }}>
        <span>Change notes (optional)</span>
        <input value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} style={field} />
      </label>
      <label style={{ ...labelStyle, marginTop: 10 }}>
        <span>Reason (audited, min 4 chars)</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} style={field} />
      </label>
      <label style={{ ...labelStyle, marginTop: 10 }}>
        <span>Your password (local accounts only)</span>
        <input
          type="password"
          autoComplete="off"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={field}
        />
      </label>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" disabled={busy || problem !== null} onClick={() => void submit()} style={primary}>
          {busy ? 'Working…' : 'Upload version'}
        </button>
        <button type="button" disabled={busy} onClick={onClose} style={secondary}>
          Cancel
        </button>
      </div>
    </div>
  );
}
